export const AUTH_CAPTCHA_SCOPE = 'tenant'

export type CaptchaConfig = {
  enabled: boolean
  siteKey: string | null
  version: 'v2' | 'v3'
}

export type ChallengeStatus = {
  captchaRequired: boolean
  recaptcha: CaptchaConfig
}

export class AuthCaptchaRequiredError extends Error {
  readonly code = 'AUTH_CAPTCHA_REQUIRED' as const
  readonly recaptcha: CaptchaConfig | null

  constructor(
    message = 'Güvenlik doğrulaması gerekli. Lütfen reCAPTCHA kutusunu işaretleyin.',
    recaptcha: CaptchaConfig | null = null,
  ) {
    super(message)
    this.name = 'AuthCaptchaRequiredError'
    this.recaptcha = recaptcha
  }
}

export class AuthCaptchaInvalidError extends Error {
  readonly code = 'AUTH_CAPTCHA_INVALID' as const
  readonly recaptcha: CaptchaConfig | null

  constructor(
    message = 'reCAPTCHA doğrulaması başarısız. Lütfen tekrar deneyin.',
    recaptcha: CaptchaConfig | null = null,
  ) {
    super(message)
    this.name = 'AuthCaptchaInvalidError'
    this.recaptcha = recaptcha
  }
}

export function isAuthCaptchaError(
  err: unknown,
): err is AuthCaptchaRequiredError | AuthCaptchaInvalidError {
  return (
    err instanceof AuthCaptchaRequiredError || err instanceof AuthCaptchaInvalidError
  )
}

function parseRecaptchaFromPayload(payload: unknown): CaptchaConfig | null {
  const data = (payload as {
    data?: {
      recaptcha?: {
        enabled?: boolean
        siteKey?: string | null
        site_key?: string | null
        version?: string
      }
    }
  }).data?.recaptcha

  if (!data) return null

  const siteKey = data.siteKey ?? data.site_key ?? null

  return {
    enabled: Boolean(data.enabled ?? siteKey),
    siteKey,
    version: data.version === 'v3' ? 'v3' : 'v2',
  }
}

export function parseAuthCaptchaPayload(
  payload: unknown,
  status: number,
): AuthCaptchaRequiredError | AuthCaptchaInvalidError | null {
  if (status !== 422 || !payload || typeof payload !== 'object') return null

  const error = (payload as { error?: Record<string, unknown> }).error
  const message = typeof error?.message === 'string' ? error.message : undefined
  const recaptcha = parseRecaptchaFromPayload(payload)

  if (error?.code === 'AUTH_CAPTCHA_REQUIRED') {
    return new AuthCaptchaRequiredError(message, recaptcha)
  }
  if (error?.code === 'AUTH_CAPTCHA_INVALID') {
    return new AuthCaptchaInvalidError(message, recaptcha)
  }
  return null
}

export function normalizeChallengeStatus(data: unknown): ChallengeStatus {
  const root = data as {
    captchaRequired?: boolean
    recaptcha?: { enabled?: boolean; siteKey?: string | null; version?: string }
  }

  return {
    captchaRequired: Boolean(root.captchaRequired),
    recaptcha: {
      enabled: Boolean(root.recaptcha?.enabled),
      siteKey: root.recaptcha?.siteKey ?? null,
      version: root.recaptcha?.version === 'v3' ? 'v3' : 'v2',
    },
  }
}
